import { useEffect, useRef, useState, type CSSProperties } from "react";
import type { CommandEndpoint } from "@shared-core/data/object-ui-adapter";
import { getAt } from "@shared-core/domain/object-ui/body-path";
import {
  holdsResult,
  resultLinksOf,
  resultWarningsOf,
} from "@shared-core/domain/object-ui/command-links";
import type {
  Choice,
  Command,
  CommandResult,
  FieldError,
  FieldHint,
} from "@shared-core/domain/object-ui/contract";
import { useCommandRun } from "@shared-core/domain/object-ui/hooks";
import {
  commandTitle,
  useObjectManifest,
} from "@shared-core/domain/object-ui/manifest";
import {
  bodyReader,
  isShown,
  isWideCommand,
  type FieldDescriptor,
} from "@shared-core/domain/object-ui/widgets";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { cn } from "../../primitives/utils";
import { useIsPhone } from "../../../useIsPhone";
import { useKeyboardInset } from "../../../useKeyboardInset";
import { AddressField } from "./AddressField";
import { fieldId, LockedField, SimpleField } from "./CommandFields";
import { isOnPaths } from "./compose-widgets";
import { CommandResultLinks } from "./CommandResultLinks";
import { ChoiceField, DryRunBlocks, ResultBlocks } from "./DryRunBlocks";
import { EntityField } from "./EntityField";
import { KeyedFilesField } from "./KeyedFilesField";
import { TableField } from "./TableField";
import type {
  CustomWidget,
  CustomWidgetRegistry,
  KnownEntities,
  OpenNestedCommand,
} from "./types";

/** A command as it reaches the shell: its served hrefs, from a record view or the manifest. */
export function endpointOf(
  command: Command,
  declared: CommandEndpoint["command"],
): CommandEndpoint {
  return {
    command: declared,
    href: command.href,
    dryRunHref: command.dry_run_href,
  };
}

const NO_HINTS: Record<string, FieldHint> = {};
/** A command with a table input or a custom widget: near the full width of a desktop screen (provisional). */
const WIDE_DIALOG = "sm:max-w-[min(80rem,calc(100vw-3rem))]";
/** "Decision, Reason and How the customer approved it": the fields the commit still waits on. */
function namesOf(labels: readonly string[]): string {
  return labels.length < 2
    ? (labels[0] ?? "")
    : `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

/** What the shell draws in the command's field order: a field, or a dry-run choice standing in for one. */
type FieldEntry =
  | { kind: "field"; field: FieldDescriptor }
  | { kind: "choice"; choice: Choice };

interface Nested {
  endpoint: CommandEndpoint;
  context: string;
  resolve: (
    outcome: { result: CommandResult; body: Record<string, unknown> } | null,
  ) => void;
}

/**
 * The command shell (brief §9.3): one dialog for every command. Inputs from
 * the fixed widget set (or a registered custom widget), a dry run kept
 * current as they change, its checks, choices and confirmations, and a
 * button named for what will happen. One dialog, one task: it offers no
 * other commands. A nested command (a create from a picker) opens a second
 * shell above this one; when it commits, this one checks again. A result with
 * per-row outcomes, links or warnings stays open until Done.
 */
export function CommandShell({
  endpoint,
  context,
  initialBody,
  lockedPaths,
  knownEntities,
  widgets,
  onClose,
  onDone,
}: {
  endpoint: CommandEndpoint;
  /** "Order ORD-K48193 · Spring catalogue reprint". */
  context: string;
  initialBody?: Record<string, unknown>;
  /** Paths of `initialBody` that came from the record this was opened from: shown, not editable. */
  lockedPaths?: readonly string[];
  /** What the page called the records `initialBody` picks, so the entity fields show names, not ids. */
  knownEntities?: KnownEntities;
  widgets: CustomWidgetRegistry;
  onClose: () => void;
  /** Committed: the body sent is given so a nested create can be read back. */
  onDone: (result: CommandResult, body: Record<string, unknown>) => void;
}) {
  const index = useObjectManifest();
  const custom = widgets[endpoint.command.id];
  const { run, snapshot } = useCommandRun(endpoint, {
    ...(initialBody ? { initialBody } : {}),
    ...(lockedPaths ? { lockedPaths } : {}),
    ...(custom ? { ownedPaths: custom.ownedPaths } : {}),
  });
  const [nested, setNested] = useState<Nested | null>(null);
  const isPhone = useIsPhone();
  const keyboardInset = useKeyboardInset();
  const loggedUnsupported = useRef(false);

  const title = commandTitle(endpoint.command.label);
  const { dryRun, body, errors, fields } = snapshot;
  const isBusy = snapshot.status === "committing";
  const read = (path: string) => getAt(body, path);
  const unsupported = fields.filter((f) => f.kind === "unsupported");
  const isWide = isWideCommand(fields, !!custom);

  useEffect(() => {
    if (unsupported.length > 0 && !loggedUnsupported.current) {
      loggedUnsupported.current = true;
      console.error("object_ui.command.unsupported_schema", {
        command: endpoint.command.id,
        fields: unsupported.map((f) => f.path),
      });
    }
  }, [endpoint.command.id, unsupported]);

  const openNested: OpenNestedCommand = (nextEndpoint, nextContext) =>
    new Promise((resolve) => {
      setNested({ endpoint: nextEndpoint, context: nextContext, resolve });
    });

  const closeNested = (
    outcome: { result: CommandResult; body: Record<string, unknown> } | null,
  ) => {
    nested?.resolve(outcome);
    setNested(null);
    if (outcome) run.retry();
  };

  const errorsFor = (path: string): FieldError[] =>
    errors.filter((e) => e.field === path || e.field.startsWith(`${path}.`));
  // A choice's errors have no input of their own: they show with the banner.
  const boundPaths = [
    ...fields.map((f) => f.path),
    ...(custom?.ownedPaths ?? []),
  ];
  const unbound = errors.filter(
    (e) =>
      !boundPaths.some((p) => e.field === p || e.field.startsWith(`${p}.`)),
  );

  const commit = async () => {
    const result = await run.commit();
    if (result && !holdsResult(result)) onDone(result, body);
  };

  const renderField = (field: FieldDescriptor) => {
    if (!isShown(field, bodyReader(body, snapshot.choices))) return null;
    if (dryRun?.field_hints[field.path]?.hidden) return null;
    const value = read(field.path);
    const fieldErrors = errorsFor(field.path);
    const disabled = isBusy;
    if (snapshot.lockedPaths.includes(field.path) && field.kind !== "group")
      return (
        <LockedField
          key={field.path}
          field={field}
          value={value}
          errors={fieldErrors}
        />
      );
    switch (field.kind) {
      case "table":
        return (
          <TableField
            key={field.path}
            field={field}
            rows={
              Array.isArray(value) ? (value as Record<string, unknown>[]) : []
            }
            rowsShown={dryRun?.field_hints[field.path]?.rows_shown ?? null}
            selectedRows={snapshot.selectedRows}
            errors={fieldErrors}
            disabled={disabled}
            onCellChange={(path, next, options) =>
              run.setValue(path, next, options)
            }
            onRowSelected={(tablePath, row, isSelected) =>
              run.setRowSelected(tablePath, row, isSelected)
            }
          />
        );
      case "address":
        return (
          <AddressField
            key={field.path}
            field={field}
            value={value}
            lookupHref={dryRun?.field_hints[field.path]?.lookup_href ?? null}
            errors={fieldErrors}
            disabled={disabled}
            onChange={(next, options) =>
              run.setValue(field.path, next, options)
            }
          />
        );
      case "entity":
        return (
          <EntityField
            key={field.path}
            field={field}
            value={value}
            errors={fieldErrors}
            disabled={disabled}
            create={dryRun?.field_hints[field.path]?.create ?? null}
            listFilters={dryRun?.field_hints[field.path]?.list_filters ?? null}
            listFilterChoices={
              dryRun?.field_hints[field.path]?.list_filter_choices ?? null
            }
            listScope={dryRun?.field_hints[field.path]?.list_scope ?? null}
            note={dryRun?.field_hints[field.path]?.note ?? null}
            knownAs={knownEntities?.[field.path] ?? null}
            valueLabel={dryRun?.field_hints[field.path]?.value_label ?? null}
            valueLabels={dryRun?.field_hints[field.path]?.value_labels ?? null}
            onChange={(next) => run.setValue(field.path, next ?? undefined)}
            onCreate={async (command) => {
              const declared = index.commandOf(command.name);
              const outcome = await openNested(
                endpointOf(command, declared),
                context,
              );
              return outcome?.result.created?.id ?? null;
            }}
          />
        );
      case "group": {
        // The object's own fields carry their errors; an error on the object itself shows under them.
        const own = fieldErrors.filter((e) => e.field === field.path);
        const errorId = `${fieldId(field.path)}-error`;
        return (
          <fieldset
            key={field.path}
            className="space-y-4 rounded-md border border-border p-3"
            {...(own.length > 0 ? { "aria-describedby": errorId } : {})}
          >
            <legend className="px-1 text-sm font-medium">
              {field.label}
              {!field.required && (
                <span className="ml-1 font-normal text-muted-foreground">
                  (optional)
                </span>
              )}
            </legend>
            {field.fields.map(renderField)}
            {own.length > 0 && (
              <p id={errorId} className="text-sm text-red-600">
                {own.map((e) => e.message).join(" ")}
              </p>
            )}
          </fieldset>
        );
      }
      case "keyed_files":
        return (
          <KeyedFilesField
            key={field.path}
            field={field}
            value={value}
            slots={dryRun?.field_hints[field.path]?.file_slots ?? null}
            errors={fieldErrors}
            disabled={disabled}
            onChange={(next) => run.setValue(field.path, next)}
          />
        );
      case "unsupported":
        return null;
      default:
        return (
          <SimpleField
            key={field.path}
            field={field}
            value={value}
            errors={fieldErrors}
            disabled={disabled}
            note={dryRun?.field_hints[field.path]?.note ?? null}
            onChange={(next, options) =>
              run.setValue(
                field.path,
                next === null ? undefined : next,
                options,
              )
            }
          />
        );
    }
  };

  // A dry-run choice whose key is a declared request field (hidden until the dry run offers it) sits where that
  // field is declared, so the dialog reads in the command's own order: Set delivery price… asks which delivery first.
  const declaredKeys = Object.keys(
    endpoint.command.request_schema?.properties ?? {},
  );
  const placedChoices = (dryRun?.choices ?? []).filter((c) =>
    declaredKeys.includes(c.key),
  );
  const inFieldOrder: FieldEntry[] = [
    ...declaredKeys.flatMap((key): FieldEntry[] => {
      const field = fields.find((f) => f.path === key);
      if (field) return [{ kind: "field", field }];
      const choice = placedChoices.find((c) => c.key === key);
      return choice ? [{ kind: "choice", choice }] : [];
    }),
    ...fields
      .filter((f) => !declaredKeys.includes(f.path))
      .map((field): FieldEntry => ({ kind: "field", field })),
  ];

  // A widget with a layout draws its parts among the fields and choices, in the layout's order; what the layout does
  // not name follows in the request's order, and a part it does not name comes last.
  type LaidOut = FieldEntry | { kind: "part"; part: CustomWidget };
  const parts = custom?.layout ? (custom.parts ?? [custom]) : [];
  const partOwning = (path: string) =>
    parts.find((p) => p.ownedPaths.includes(path));
  const named = new Set<string>();
  const laidOut: LaidOut[] = custom?.layout
    ? (() => {
        const entries: LaidOut[] = [];
        const placedParts = new Set<CustomWidget>();
        for (const key of custom.layout!) {
          if (key.startsWith("@")) {
            const part = partOwning(key.slice(1));
            if (part && !placedParts.has(part)) {
              placedParts.add(part);
              entries.push({ kind: "part", part });
            }
            continue;
          }
          const entry = inFieldOrder.find((e) =>
            e.kind === "field" ? e.field.path === key : e.choice.key === key,
          );
          if (entry) {
            named.add(key);
            entries.push(entry);
          }
        }
        for (const entry of inFieldOrder) {
          const key =
            entry.kind === "field" ? entry.field.path : entry.choice.key;
          if (!named.has(key)) entries.push(entry);
        }
        for (const part of parts)
          if (!placedParts.has(part)) entries.push({ kind: "part", part });
        // Until the awaited value is there, nothing after the part that owns it is drawn.
        const waitsFor = custom.layoutWaitsFor;
        const awaited = waitsFor ? body[waitsFor] : "ready";
        if (awaited !== undefined && awaited !== null) return entries;
        const gate = entries.findIndex(
          (e) => e.kind === "part" && e.part.ownedPaths.includes(waitsFor!),
        );
        return gate === -1 ? entries : entries.slice(0, gate + 1);
      })()
    : [];

  const result = snapshot.result;
  const showsResults = !!result && holdsResult(result);
  const links = result ? resultLinksOf(result) : [];
  const warnings = result ? resultWarningsOf(result) : [];
  // A widget placed after the choices reads what they chose, so it waits for the dry run that offers them.
  const isAfterChoices = custom?.placement === "after_choices";
  const isAfterFields = custom?.placement === "after_fields";
  const customWidget = custom ? (
    <custom.Component
      body={body}
      setValues={(values, options) => run.setValues(values, options)}
      errors={errors.filter((e) => isOnPaths(e, custom.ownedPaths))}
      hints={dryRun?.field_hints ?? NO_HINTS}
      dryRun={dryRun}
      disabled={isBusy}
      openNested={openNested}
      recheck={() => run.retry()}
    />
  ) : null;

  const keyboardAbove: CSSProperties | undefined =
    isPhone && keyboardInset > 0
      ? {
          top: "auto",
          bottom: keyboardInset,
          translate: "-50% 0",
          maxHeight: `calc(100dvh - ${keyboardInset}px - 1rem)`,
        }
      : undefined;

  return (
    <>
      <Dialog open onOpenChange={(open) => !open && !nested && onClose()}>
        {/* dvh, not vh: on a phone the browser bars take part of 100vh. On a
            phone the footer sticks to the bottom of the scrolling dialog, so
            the commit button stays in reach however long the fields run.
            iOS leaves the page its full height under the keyboard, so while
            one is up the dialog sits on top of it instead of centred, which
            brings that footer up with it. */}
        <DialogContent
          className={cn(
            "max-h-[90dvh] overflow-y-auto max-sm:p-4",
            isWide ? WIDE_DIALOG : "sm:max-w-2xl",
          )}
          style={keyboardAbove}
        >
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>{context}</DialogDescription>
          </DialogHeader>

          {showsResults && result ? (
            <div className="space-y-3">
              <p role="status" className="text-sm font-medium">
                {result.summary}
              </p>
              {warnings.length > 0 && (
                <ul className="space-y-1 text-sm text-amber-700 dark:text-amber-400">
                  {warnings.map((warning) => (
                    <li key={warning.code}>{warning.message}</li>
                  ))}
                </ul>
              )}
              {links.length > 0 && <CommandResultLinks links={links} />}
              {result.display && result.display.length > 0 && (
                <ResultBlocks blocks={result.display} />
              )}
              {result.rows && (
                <ul className="divide-y divide-border text-sm">
                  {result.rows.map((row) => (
                    <li key={row.target.id} className="flex gap-3 py-1.5">
                      <span className="font-medium tabular-nums">
                        {row.label ?? row.target.id}
                      </span>
                      <span
                        className={
                          row.ok
                            ? "text-green-700 dark:text-green-400"
                            : "text-red-700 dark:text-red-400"
                        }
                      >
                        {row.ok ? "Done" : row.error}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              {snapshot.banner && (
                <div
                  role="alert"
                  className={
                    snapshot.banner.tone === "warning"
                      ? "rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/30 dark:text-amber-100"
                      : "rounded-md bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-900/30 dark:text-red-100"
                  }
                >
                  <p>{snapshot.banner.message}</p>
                  {snapshot.status === "check_failed" && (
                    <button
                      type="button"
                      className="mt-1 text-sm underline"
                      onClick={() => run.retry()}
                    >
                      Check again
                    </button>
                  )}
                </div>
              )}
              {unbound.length > 0 && (
                <ul className="text-sm text-red-600">
                  {unbound.map((e) => (
                    <li key={`${e.field}-${e.code}`}>{e.message}</li>
                  ))}
                </ul>
              )}
              {unsupported.length > 0 ? (
                <p className="text-sm text-muted-foreground">
                  This command can't be run here yet.
                </p>
              ) : (
                <>
                  {custom?.layout && (
                    <div className="space-y-4">
                      {laidOut.map((entry, n) =>
                        entry.kind === "part" ? (
                          <entry.part.Component
                            key={`part-${n}`}
                            body={body}
                            setValues={(
                              values: Record<string, unknown>,
                              options?: {
                                debounce?: boolean;
                                refill?: boolean;
                              },
                            ) => run.setValues(values, options)}
                            errors={errors.filter((e) =>
                              isOnPaths(e, entry.part.ownedPaths),
                            )}
                            hints={dryRun?.field_hints ?? NO_HINTS}
                            dryRun={dryRun}
                            disabled={isBusy}
                            openNested={openNested}
                            recheck={() => run.retry()}
                          />
                        ) : entry.kind === "field" ? (
                          renderField(entry.field)
                        ) : (
                          <ChoiceField
                            key={`choice-${entry.choice.key}`}
                            choice={entry.choice}
                            picked={snapshot.choices[entry.choice.key]}
                            disabled={isBusy}
                            onChoose={(key, value) => run.choose(key, value)}
                            onChooseMany={(key, values) =>
                              run.chooseMany(key, values)
                            }
                            search={snapshot.searches[entry.choice.key]}
                            isSearchPending={snapshot.isPending}
                            onSearch={(key, text) => run.setSearch(key, text)}
                          />
                        ),
                      )}
                    </div>
                  )}
                  {!custom?.layout &&
                    !isAfterChoices &&
                    !isAfterFields &&
                    customWidget}
                  {!custom?.layout &&
                    (fields.length > 0 || placedChoices.length > 0) && (
                      <div className="space-y-4">
                        {inFieldOrder.map((entry) =>
                          entry.kind === "field" ? (
                            renderField(entry.field)
                          ) : (
                            <ChoiceField
                              key={`choice-${entry.choice.key}`}
                              choice={entry.choice}
                              picked={snapshot.choices[entry.choice.key]}
                              disabled={isBusy}
                              onChoose={(key, value) => run.choose(key, value)}
                              onChooseMany={(key, values) =>
                                run.chooseMany(key, values)
                              }
                              search={snapshot.searches[entry.choice.key]}
                              isSearchPending={snapshot.isPending}
                              onSearch={(key, text) => run.setSearch(key, text)}
                            />
                          ),
                        )}
                      </div>
                    )}
                  {!custom?.layout && isAfterFields && customWidget}
                  {dryRun && (
                    <DryRunBlocks
                      dryRun={dryRun}
                      afterChoices={isAfterChoices ? customWidget : null}
                      placedChoiceKeys={placedChoices.map((c) => c.key)}
                      choices={snapshot.choices}
                      confirmed={snapshot.confirmed}
                      disabled={isBusy}
                      hrefOf={(ref) => index.hrefOf(ref)}
                      onChoose={(key, value) => run.choose(key, value)}
                      onChooseMany={(key, values) =>
                        run.chooseMany(key, values)
                      }
                      searches={snapshot.searches}
                      isSearchPending={snapshot.isPending}
                      onSearch={(key, text) => run.setSearch(key, text)}
                      onConfirm={(key, isConfirmed) =>
                        run.setConfirmed(key, isConfirmed)
                      }
                      onAskAgain={() => run.retry()}
                      onUseBestPriced={(use) => run.setChosen(use)}
                    />
                  )}
                </>
              )}
              {/* A setting (a command that edits a fact) is changed back by its pair or by itself: never "cannot be undone". */}
              {endpoint.command.destructive && !endpoint.command.edits && (
                <p className="text-sm text-red-700 dark:text-red-400">
                  This cannot be undone.
                </p>
              )}
            </div>
          )}

          <DialogFooter className="items-center gap-3 max-sm:sticky max-sm:bottom-0 max-sm:-mx-4 max-sm:-mb-4 max-sm:flex-col max-sm:items-stretch max-sm:border-t max-sm:bg-background max-sm:px-4 max-sm:py-3 sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              {!showsResults && (
                <span role="status" className="text-xs text-muted-foreground">
                  {snapshot.status === "check_failed"
                    ? ""
                    : snapshot.isPending || snapshot.status === "checking"
                      ? "Checking…"
                      : snapshot.unfilledFields.length > 0
                        ? `Fill in ${namesOf(snapshot.unfilledFields)} to continue.`
                        : "Checked"}
                </span>
              )}
            </div>
            <div className="flex gap-2 max-sm:[&>button]:h-10 max-sm:[&>button]:flex-1">
              {showsResults && result ? (
                <Button type="button" onClick={() => onDone(result, body)}>
                  Done
                </Button>
              ) : (
                <>
                  <Button type="button" variant="outline" onClick={onClose}>
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    variant={
                      endpoint.command.destructive ? "destructive" : "default"
                    }
                    disabled={!snapshot.canCommit || isBusy}
                    onClick={() => void commit()}
                  >
                    {dryRun?.effect ?? title}
                  </Button>
                </>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {nested && (
        <CommandShell
          endpoint={nested.endpoint}
          context={nested.context}
          widgets={widgets}
          onClose={() => closeNested(null)}
          onDone={(nestedResult, nestedBody) =>
            closeNested({ result: nestedResult, body: nestedBody })
          }
        />
      )}
    </>
  );
}
